import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { SeoAnalysisResult, SeoAnalyzerResponse, SeoAnalyzerError } from '../../../types/seoAnalyzer';
import { fetchApi } from '../../../utils/api';

const SEOAnalyzerTab: React.FC = () => {
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<SeoAnalysisResult | null>(null);
  const [error, setError] = useState('');
  
  const { user } = useAuth();
  
  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };
  
  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }
    
    if (!validateUrl(url)) {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    
    setError('');
    setIsAnalyzing(true);
    setAnalysisResults(null);
    
    try {
      const data: SeoAnalyzerResponse | SeoAnalyzerError = await fetchApi('api/marketing/seo-analyze', {
        method: 'POST',
        body: JSON.stringify({ 
          url,
          userId: user?.id 
        }),
      });
      
      if ('error' in data) {
        throw new Error(data.error || 'Failed to analyze website');
      }

      setAnalysisResults(data.analysis);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze the website. Please try again.');
      console.error(err);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Website SEO Analysis</h3>
        <p className="text-gray-600 text-sm">
          Enter a URL to analyze on-page SEO, technical performance, and get actionable recommendations.
        </p>
      </div>

      <form onSubmit={handleAnalyze} className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-grow">
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
              Website URL
            </label>
            <input
              type="text"
              id="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={isAnalyzing}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition ${isAnalyzing ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {isAnalyzing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing...
                </span>
              ) : 'Analyze'}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-red-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {analysisResults && (
        <div className="space-y-6">
          {/* SEO Score */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Overall SEO Score</h4>
                <p className="text-sm text-gray-600">Based on multiple factors</p>
              </div>
              <div className="text-5xl font-bold text-green-600">
                {analysisResults.overallScore || 'N/A'}
                <span className="text-2xl text-gray-500">/100</span>
              </div>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(analysisResults).map(([key, value]) => {
              if (key === 'overallScore') return null;
              
              return (
                <div key={key} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <h5 className="font-semibold text-gray-900 mb-2 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h5>
                  <div className="text-sm text-gray-700">
                    {typeof value === 'object' ? (
                      <pre className="whitespace-pre-wrap font-mono text-xs bg-gray-50 p-2 rounded">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      <p>{String(value)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!analysisResults && !error && !isAnalyzing && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No analysis yet</h3>
          <p className="mt-1 text-sm text-gray-500">Enter a URL above to get started</p>
        </div>
      )}
    </div>
  );
};

export default SEOAnalyzerTab;
